import { useRef, useState } from "react";
import {
  Box,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { useAppDispatch } from "hooks/redux";
import { snackbarRequest } from "shared/molecules/snackbar/snackbar-slice";
import liveClassClientService from "utils/graphql/liveclassClient";
import { CREATE_STUDENT_SESSION_ALLOCATIONS } from "utils/graphql/services/live-classes/mutations";

type ConfirmDialogState = {
  open: boolean;
  pendingRow: any;
  affectedIds: string[];
};

export function useCreateSessionAllocation() {
  const dispatch = useAppDispatch();
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    open: false,
    pendingRow: null,
    affectedIds: [],
  });
  const dialogTitleRef = useRef<HTMLElement | null>(null);

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, pendingRow: null, affectedIds: [] });
  };

  const [createSessionAllocations, { loading: allocating }] = useMutation(
    CREATE_STUDENT_SESSION_ALLOCATIONS,
    { client: liveClassClientService },
  );

  // isAllowed is false for the initial request and true when the user confirms.
  async function callCreateSessionAllocations(row: any, isAllowed = false) {
    const input: any = {
      projectIds: (row?.liveSession?.projects || [])
        .map((project: any) => Number(project?.id))
        .filter((id: number) => Number.isFinite(id)),
      vendorId: row?.liveSession?.vendorId,
      liveSessionId: Number(row?.liveSessionId),
      liveclassPackageIds: (row?.liveclassPackages || [])
        .filter((pkg: any) => pkg?.isActive === true)
        .map((pkg: any) => Number(pkg.id)),
      isAllowed,
    };

    return createSessionAllocations({ variables: { input } });
  }

  async function handleSessionAllocations(row: any) {
    try {
      const response = await callCreateSessionAllocations(row, false);
      if (response?.data?.createSessionAllocations) {
        dispatch(
          snackbarRequest({
            message:
              response.data.createSessionAllocations.message ||
              "Session allocated successfully",
          }),
        );
      }
    } catch (err: any) {
      const graphQLError = err?.graphQLErrors?.[0];
      const errorCode = graphQLError?.extensions?.error_code;
      const errorMessage: string =
        graphQLError?.message || "Failed to allocate session";

      if (errorCode === "GOOGLE_WORKSPACE_MAPPING_NOT_FOUND") {
        const data = graphQLError?.extensions?.data;
        const ids: string[] = Array.isArray(data)
          ? data.map(String)
          : data?.admissionNumbers?.map(String) ||
            data?.profileIds?.map(String) ||
            data?.ProfileIds?.map(String) ||
            [];

        setConfirmDialog({ open: true, pendingRow: row, affectedIds: ids });
      } else {
        dispatch(snackbarRequest({ message: errorMessage, type: "error" }));
      }
    }
  }

  async function handleConfirmContinue() {
    const { pendingRow } = confirmDialog;

    try {
      const response = await callCreateSessionAllocations(pendingRow, true);
      closeConfirmDialog();
      if (response?.data?.createSessionAllocations) {
        dispatch(
          snackbarRequest({
            message:
              response.data.createSessionAllocations.message ||
              "Session allocated successfully",
          }),
        );
      }
    } catch (err: any) {
      const errorMessage =
        err?.graphQLErrors?.[0]?.message || "Failed to allocate session";
      dispatch(snackbarRequest({ message: errorMessage, type: "error" }));
      closeConfirmDialog();
    }
  }

const dialogs = (
  <Dialog
    open={confirmDialog.open}
    onClose={closeConfirmDialog}
    disableScrollLock
    disableAutoFocus
    TransitionProps={{
      onEntered: () => dialogTitleRef.current?.focus({ preventScroll: true }),
    }}
    maxWidth="sm"
    fullWidth
  >
    <DialogTitle ref={dialogTitleRef} tabIndex={-1} sx={{ fontWeight: 700 }}>
      Google Workspace Account Missing
    </DialogTitle>

    <DialogContent dividers>
      <Typography variant="body1" gutterBottom>
        Some selected students do not have an active Google Workspace account.
        Without an active account, these students will not be able to attend
        their live classes.
        <br />
        <Typography
          component="span"
          sx={{ fontWeight: 500, display: "block", mt: 1 }}
        >
          Do you want to continue with session allocation anyway?
        </Typography>
      </Typography>

      {confirmDialog.affectedIds.length > 0 && (
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            bgcolor: "#fff3e0",
            borderRadius: 1,
            border: "1px solid #ffb74d",
            maxHeight: 150,
            overflowY: "auto",
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
          >
            Affected Student IDs:
          </Typography>

          <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
            {confirmDialog.affectedIds.join(", ")}
          </Typography>
        </Box>
      )}
    </DialogContent>

    <DialogActions sx={{ p: 2, gap: 1 }}>
      <MuiButton
        variant="outlined"
        color="inherit"
        disabled={allocating}
        onClick={closeConfirmDialog}
      >
        Cancel
      </MuiButton>

      <MuiButton
        variant="contained"
        color="primary"
        disabled={allocating}
        onClick={handleConfirmContinue}
      >
        {allocating ? "Allocating..." : "Continue"}
      </MuiButton>
    </DialogActions>
  </Dialog>
);

  return { handleSessionAllocations, dialogs };
}
